import React, { useEffect, useState } from "react";
import { Gift, Copy, Check, Users } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";

const Referrals = () => {
  const { t } = useT();
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => { api("/referrals/stats").then(setStats).catch(() => setStats(false)); }, []);

  const shareUrl = stats && stats.code ? `${window.location.origin}/register?ref=${stats.code}` : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success(t("p2.ref.copied"));
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error(t("p2.ref.copyFail")); }
  };

  return (
    <div>
      <PageHeader icon={Gift} title={t("p2.ref.title")} subtitle={t("p2.ref.subtitle")} />
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        {stats === null ? (
          <div className="h-64 animate-pulse rounded-3xl bg-slate-100" />
        ) : !stats ? (
          <p className="text-center text-slate-500">{t("common.loadFail")}</p>
        ) : (
          <>
            <div className="rounded-3xl border border-border bg-gradient-to-br from-brandblue to-navy p-8 text-white shadow-xl">
              <div className="flex items-center gap-2 text-sm text-white/80">
                <Gift size={16} /> {t("p2.ref.linkLabel")}
              </div>
              <div className="mt-3 flex items-center gap-2 rounded-2xl bg-white/10 p-2 ring-1 ring-white/15">
                <input readOnly value={shareUrl} data-testid="referral-link" className="w-full bg-transparent px-3 py-2 text-sm outline-none" />
                <Button onClick={copy} data-testid="referral-copy" className="rounded-xl bg-white text-navy hover:bg-white/90">
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                </Button>
              </div>
              <p className="mt-4 text-sm text-white/70">
                {t("p2.ref.reward", { invitee: stats.invitee_reward_days, req: stats.referrals_required_per_reward, referrer: stats.referrer_reward_days })}
              </p>
            </div>

            <div className="mt-6 rounded-3xl border border-border bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="font-display font-bold text-navy">{t("p2.ref.progress")}</span>
                <span className="text-sm font-bold text-brandblue">{stats.progress_current}/{stats.progress_target}</span>
              </div>
              <Progress value={(stats.progress_current / stats.progress_target) * 100} className="mt-3 h-2.5" />
              <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                <div className="rounded-2xl bg-slate-50 p-4">
                  <div className="font-display text-2xl font-extrabold text-navy">{stats.total_invited}</div>
                  <div className="text-xs text-slate-500">{t("p2.ref.invited")}</div>
                </div>
                <div className="rounded-2xl bg-slate-50 p-4">
                  <div className="font-display text-2xl font-extrabold text-success">{stats.qualified}</div>
                  <div className="text-xs text-slate-500">{t("p2.ref.qualified")}</div>
                </div>
                <div className="rounded-2xl bg-slate-50 p-4">
                  <div className="font-display text-2xl font-extrabold text-brandblue">{stats.premium_earned_days}</div>
                  <div className="text-xs text-slate-500">{t("p2.ref.premiumDays")}</div>
                </div>
              </div>
            </div>

            {stats.recent?.length > 0 && (
              <div className="mt-6 rounded-3xl border border-border bg-white p-6 shadow-sm">
                <h3 className="flex items-center gap-2 font-display font-bold text-navy"><Users size={18} /> {t("p2.ref.recent")}</h3>
                <ul className="mt-4 space-y-2">
                  {stats.recent.map((r, i) => (
                    <li key={i} className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-2.5 text-sm">
                      <span className="text-slate-700">{r.invitee_email || t("p2.ref.unknown")}</span>
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${r.status === "qualified" ? "bg-success/15 text-success" : "bg-slate-200 text-slate-500"}`}>
                        {r.status === "qualified" ? t("p2.ref.qualified") : t("p2.ref.pending")}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Referrals;
